import { type EPLITrainerDevelopmentPlanAttendee, type EPLITrainerDevelopmentPlan } from '../models/EPLITrainerDevelopmentPlan'
import { type Dispatch, useEffect, useState } from 'react'
import {
  type AttendeeFormValues,
  type UpdateEPLITrainerDevelopmentPlanFormValues
} from '../pages/pageComponents/planning/EPLITrainerDevelopmentPlanModal'
import { GetEPLITrainerDevelopmentPlans, LockActivity, UnlockActivity } from '../services/AnnualPlanService'
import { PriorityOneActivityName } from '../models/PriorityOneActivityName'

interface UseEPLITrainerDevelopmentPlansType {
  epliTrainerDevelopmentPlans: EPLITrainerDevelopmentPlan[]
  isLoading: boolean
  reloadEPLITrainerDevelopmentPlans: () => Promise<void>
}
export const useEPLITrainerDevelopmentPlans = (priorityId: number, isImplementationPhase?: boolean): UseEPLITrainerDevelopmentPlansType => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [epliTrainerDevelopmentPlans, setEPLITrainerDevelopmentPlans] = useState<EPLITrainerDevelopmentPlan[]>([])
  const loadEPLITrainerDevelopmentPlans = async (): Promise<void> => {
    setIsLoading(true)

    const epliTrainerDevelopmentPlans = await GetEPLITrainerDevelopmentPlans(priorityId, isImplementationPhase !== true)
    setEPLITrainerDevelopmentPlans(epliTrainerDevelopmentPlans)

    setIsLoading(false)
  }

  useEffect(() => {
    void loadEPLITrainerDevelopmentPlans()
  }, [])

  return {
    isLoading,
    epliTrainerDevelopmentPlans,
    reloadEPLITrainerDevelopmentPlans: loadEPLITrainerDevelopmentPlans
  }
}

interface UseEPLITrainerDevelopmentPlanModalType {
  handleAddEPLITrainerDevelopmentPlan: () => void
  handleEditEPLITrainerDevelopmentPlan: (epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan) => Promise<void>
  handleCloseModal: (isUpdate: boolean) => Promise<void>
  showEPLITrainerDevelopmentPlanModal: boolean
  initialValues?: UpdateEPLITrainerDevelopmentPlanFormValues
  handleTrackEPLITrainerDevelopmentPlan: (epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan) => Promise<void>
  epliTrainerDevelopmentPlanToTrack: EPLITrainerDevelopmentPlan | null
  handleCloseTrackingModal: () => Promise<void>
  attendeeToFormValues: (attendee: EPLITrainerDevelopmentPlanAttendee) => AttendeeFormValues
  startOnDetailsPage: boolean
  setStartOnDetailsPage: Dispatch<React.SetStateAction<boolean>>
}
export const useEPLITrainerDevelopmentPlanModal = (): UseEPLITrainerDevelopmentPlanModalType => {
  const [initialValues, setInitialValues] = useState<UpdateEPLITrainerDevelopmentPlanFormValues>()
  const [showEPLITrainerDevelopmentPlanModal, setShowEPLITrainerDevelopmentPlanModal] = useState(false)
  const [epliTrainerDevelopmentPlanToTrack, setEPLITrainerDevelopmentPlanToTrack] = useState<EPLITrainerDevelopmentPlan | null>(null)
  const [startOnDetailsPage, setStartOnDetailsPage] = useState<boolean>(false)

  const attendeeToFormValues = (attendee: EPLITrainerDevelopmentPlanAttendee): AttendeeFormValues => ({
    id: attendee.id ?? undefined,
    fullName: attendee.fullName,
    isdId: attendee.isd.id,
    isAttendeeFromDistrict: attendee.district != null,
    districtId: attendee.district?.id,
    status: attendee.status,
    completionStatus: attendee.completionStatus ?? null
  })

  const handleAddEPLITrainerDevelopmentPlan = (): void => {
    setInitialValues(undefined)
    setShowEPLITrainerDevelopmentPlanModal(true)
  }
  const handleEditEPLITrainerDevelopmentPlan = async (epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan): Promise<void> => {
    setInitialValues({
      ...epliTrainerDevelopmentPlan,
      epliTrainingId: epliTrainerDevelopmentPlan.epliTraining.id ?? null,
      attendees: epliTrainerDevelopmentPlan.attendees?.map(attendeeToFormValues) ?? []
    })
    setShowEPLITrainerDevelopmentPlanModal(true)
    await LockActivity(
      epliTrainerDevelopmentPlan.id,
      PriorityOneActivityName.EpliPlan,
      epliTrainerDevelopmentPlan.isUpdate
    )
  }

  const handleCloseModal = async (isUpdate: boolean): Promise<void> => {
    setStartOnDetailsPage(false)
    setShowEPLITrainerDevelopmentPlanModal(false)
    if (initialValues != null) {
      await UnlockActivity(initialValues.id, PriorityOneActivityName.EpliPlan, isUpdate)
    }
  }

  const handleTrackEPLITrainerDevelopmentPlan = async (epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan): Promise<void> => {
    setEPLITrainerDevelopmentPlanToTrack(epliTrainerDevelopmentPlan)

    await LockActivity(epliTrainerDevelopmentPlan.id, PriorityOneActivityName.EpliPlan, epliTrainerDevelopmentPlan.isUpdate)
  }

  const handleCloseTrackingModal = async (): Promise<void> => {
    setEPLITrainerDevelopmentPlanToTrack(null)

    if (epliTrainerDevelopmentPlanToTrack != null) {
      await UnlockActivity(
        epliTrainerDevelopmentPlanToTrack.id,
        PriorityOneActivityName.EpliPlan,
        epliTrainerDevelopmentPlanToTrack?.isUpdate
      )
    }
  }

  return {
    initialValues,
    handleCloseModal,
    showEPLITrainerDevelopmentPlanModal,
    handleAddEPLITrainerDevelopmentPlan,
    handleEditEPLITrainerDevelopmentPlan,
    handleTrackEPLITrainerDevelopmentPlan,
    epliTrainerDevelopmentPlanToTrack,
    handleCloseTrackingModal,
    attendeeToFormValues,
    startOnDetailsPage,
    setStartOnDetailsPage
  }
}
